/**
 * RecipientPortalPage  /preview/:token
 *
 * Public — no auth required. Shows the card recipient:
 *   • A visual preview of the card front (matching the physical design)
 *   • The handwritten message inside
 *   • Delivery status (preparing → in transit → delivered)
 *   • Occasion countdown (days until the occasion)
 *   • An emoji reaction they can leave for the sender
 *   • A gentle CTA to create their own MomentSent account
 *
 * Token expires 30 days after card is sent.
 */

import { useEffect, useState } from 'react';
import { useParams, Link }     from 'react-router-dom';
import CardPreviewFront        from '../components/portal/CardPreviewFront';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PortalData {
  contact_name:         string;
  sender_first_name:    string;
  occasion_type:        string | null;
  custom_occasion_name: string | null;
  occasion_date:        string | null;  // YYYY-MM-DD
  recurring:            boolean;
  message:              string | null;
  delivery_status:      'preparing' | 'in_transit' | 'delivered';
  sent_at:              string | null;
  delivered_at:         string | null;
  reaction_emoji:       string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const REACTIONS = ['❤️', '😊', '🥹', '🙏', '🎉', '😍', '🤩'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function daysUntil(dateStr: string): number {
  const today     = new Date();
  today.setHours(0, 0, 0, 0);
  const target    = new Date(dateStr + 'T00:00:00');
  const diffMs    = target.getTime() - today.getTime();
  return Math.ceil(diffMs / 86_400_000);
}

/** For recurring occasions (MM-DD fixed), compute next occurrence's year. */
function nextOccurrenceDate(mmdd: string): string {
  const [, m, d]  = mmdd.match(/^(\d{4})-(\d{2})-(\d{2})$/) ?? [];
  const today      = new Date();
  const thisYear   = today.getFullYear();
  const candidate  = new Date(`${thisYear}-${m}-${d}T00:00:00`);
  if (candidate < today) candidate.setFullYear(thisYear + 1);
  return candidate.toISOString().split('T')[0];
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

function StatusStep({
  icon, label, sublabel, active, done,
}: { icon: string; label: string; sublabel?: string; active: boolean; done: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div className={[
        'w-9 h-9 rounded-full flex items-center justify-center text-base flex-shrink-0 border-2 transition-all',
        done   ? 'bg-sage border-sage text-navy' :
        active ? 'bg-white border-navy text-navy' :
                 'bg-white border-gray-200 text-gray-300',
      ].join(' ')}>
        {done ? '✓' : icon}
      </div>
      <div className="pt-1">
        <p className={`text-sm font-semibold ${active || done ? 'text-navy' : 'text-gray-400'}`}>{label}</p>
        {sublabel && <p className="text-xs text-gray-500 mt-0.5">{sublabel}</p>}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function RecipientPortalPage() {
  const { token } = useParams<{ token: string }>();

  const [portal,   setPortal]   = useState<PortalData | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [reaction, setReaction] = useState<string | null>(null);
  const [reacting, setReacting] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/recipient-portal/${token}`)
      .then((r) => r.json())
      .then((json) => {
        if (json.error) throw new Error(json.error);
        setPortal(json.portal);
        setReaction(json.portal.reaction_emoji);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  async function sendReaction(emoji: string) {
    if (reacting || reaction) return;
    setReacting(true);
    try {
      const res = await fetch(`/api/recipient-portal/${token}/react`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ emoji }),
      });
      const json = await res.json();
      if (res.ok) setReaction(json.emoji);
    } catch { /* silent */ } finally {
      setReacting(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Error / expired ───────────────────────────────────────────────────────────

  if (error || !portal) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6">
        <div className="max-w-sm text-center">
          <div className="text-5xl mb-4">✉️</div>
          <h1 className="font-heading text-2xl text-navy mb-3">
            {error?.includes('expired') ? 'Preview link expired' : 'Preview not found'}
          </h1>
          <p className="text-gray-500 text-sm font-body mb-6">
            {error?.includes('expired')
              ? 'This preview link was valid for 30 days after the card was sent.'
              : 'This link may be invalid. Check with the person who sent you the card.'}
          </p>
          <Link to="/" className="text-sage text-sm font-medium hover:underline">
            Learn about MomentSent →
          </Link>
        </div>
      </div>
    );
  }

  // ── Compute countdown ─────────────────────────────────────────────────────────

  let targetDate  = portal.occasion_date ?? null;
  let daysLeft    = null as number | null;

  if (targetDate) {
    if (portal.recurring) {
      targetDate = nextOccurrenceDate(targetDate);
    }
    daysLeft = daysUntil(targetDate);
  }

  const { delivery_status } = portal;

  return (
    <div className="min-h-screen bg-cream">

      {/* Slim nav */}
      <nav className="sticky top-0 z-10 bg-cream/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="font-heading text-base font-semibold text-navy">
            MomentSent
          </Link>
          <Link to="/signup"
            className="text-xs font-medium text-sage hover:underline">
            Send your own cards →
          </Link>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-4 py-10 space-y-8">

        {/* ── Intro ─────────────────────────────────────────────────────── */}
        <div className="text-center">
          <p className="text-sm text-gray-500 font-body mb-1">
            {portal.sender_first_name} sent you something special
          </p>
          <h1 className="font-heading text-3xl text-navy">
            Hey {portal.contact_name.split(' ')[0]} 👋
          </h1>
          {daysLeft !== null && daysLeft > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-navy/5 rounded-full px-4 py-1.5">
              <span className="text-lg">🗓️</span>
              <span className="text-sm font-medium text-navy">
                {daysLeft === 1 ? 'Tomorrow!' : `${daysLeft} days away`}
                {targetDate && (
                  <span className="text-gray-500 font-normal"> · {formatDate(targetDate)}</span>
                )}
              </span>
            </div>
          )}
          {daysLeft !== null && daysLeft <= 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-sage/10 rounded-full px-4 py-1.5">
              <span className="text-lg">🎉</span>
              <span className="text-sm font-medium text-sage">Today's the day!</span>
            </div>
          )}
        </div>

        {/* ── Card preview ──────────────────────────────────────────────── */}
        <div className="flex justify-center">
          <CardPreviewFront
            occasionType={portal.occasion_type ?? 'thinking_of_you'}
            contactName={portal.contact_name}
            customOccasionName={portal.custom_occasion_name}
          />
        </div>

        {/* ── Message ───────────────────────────────────────────────────── */}
        {portal.message && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
              Message inside the card
            </p>
            <div className="flex gap-3">
              <div className="w-0.5 bg-sage/40 rounded-full flex-shrink-0" />
              <p className="font-accent italic text-navy text-base leading-relaxed">
                "{portal.message}"
              </p>
            </div>
            <p className="text-right text-sm font-heading text-sage mt-3">
             {portal.sender_first_name}
            </p>
          </div>
        )}

        {/* ── Delivery status ───────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">
            Delivery status
          </p>
          <div className="space-y-4">
            <StatusStep
              icon="🖨️"
              label="Being prepared"
              sublabel="Your card is at the print shop"
              active={delivery_status === 'preparing'}
              done={delivery_status !== 'preparing'}
            />
            <div className="ml-4 w-0.5 h-4 bg-gray-100" />
            <StatusStep
              icon="📬"
              label="In the mail"
              sublabel={portal.sent_at
                ? `Sent ${new Date(portal.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'On its way to you'}
              active={delivery_status === 'in_transit'}
              done={delivery_status === 'delivered'}
            />
            <div className="ml-4 w-0.5 h-4 bg-gray-100" />
            <StatusStep
              icon="🏠"
              label="Delivered"
              sublabel={portal.delivered_at
                ? `Arrived ${new Date(portal.delivered_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                : 'Estimated 3 to 7 business days'}
              active={delivery_status === 'delivered'}
              done={delivery_status === 'delivered'}
            />
          </div>
        </div>

        {/* ── Reaction ──────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-6 py-5">
          {reaction ? (
            <div className="text-center py-2">
              <div className="text-4xl mb-2">{reaction}</div>
              <p className="text-sm text-gray-500 font-body">
                You reacted, {portal.sender_first_name} will love it!
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-navy mb-3 text-center">
                Send {portal.sender_first_name} a reaction ✨
              </p>
              <div className="flex justify-center gap-3 flex-wrap">
                {REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => sendReaction(emoji)}
                    disabled={reacting}
                    className="text-2xl rounded-xl p-2.5 border-2 border-transparent
                               hover:border-sage hover:bg-sage/5 transition-all
                               disabled:opacity-50 active:scale-110"
                    title={`React with ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── CTA ───────────────────────────────────────────────────────── */}
        <div className="rounded-2xl bg-navy px-6 py-6 text-center">
          <p className="font-heading text-xl text-white mb-2">
            Never forget a birthday again
          </p>
          <p className="text-sm text-white/70 font-body mb-4">
            MomentSent remembers the people who matter and automatically sends
            beautiful cards on your behalf, starting at $7.99/month.
          </p>
          <Link
            to="/signup"
            className="inline-block bg-gold text-navy font-semibold text-sm
                       px-6 py-2.5 rounded-full hover:bg-gold/90 transition-colors"
          >
            Try it free for 14 days →
          </Link>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 pb-4">
          This card was sent via{' '}
          <Link to="/" className="text-sage hover:underline">MomentSent</Link>,
          automated cards for people who care.
        </p>
      </div>
    </div>
  );
}
